// 🎁 src/services/free-code-service.js — Unified + Robust
// - Works with Firebase compat (window.firebase/window.db)
// - Frontend/Backend fanType normalization
// - Safe Timestamp handling (Firestore/JS Date)
// - Defensive retries and clear error messages
// - Smart invite links: /?free=<CODE>

import { logger } from '../utils/logger';
import { FAN_TYPE_MAPPINGS } from '../utils/unified-credit-config';

class FreeCodeService {
  constructor() {
    this.db = null;
    this.initialized = false;
    this.FieldValue = null;
    this.Timestamp = null;
  }

  // Waits for compat Firebase to be ready (auth/db)
  async #waitForFirebase(timeoutMs = 10000) {
    const started = Date.now();
    while (
      !(typeof window !== 'undefined' && window.firebase && window.db) &&
      Date.now() - started < timeoutMs
    ) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise(r => setTimeout(r, 100));
    }
    if (!(window?.firebase && window?.db)) {
      throw new Error('Firebase database not available after timeout');
    }
  }

  async initialize() {
    if (this.initialized) return;

    try {
      await this.#waitForFirebase();
      this.db = window.db;

      // Convenience aliases if present
      this.FieldValue = window.firebase.firestore.FieldValue || null;
      this.Timestamp = window.firebase.firestore.Timestamp || null;

      this.initialized = true;
      logger.success('✅ FreeCodeService initialized');
    } catch (error) {
      console.error('❌ FreeCodeService initialization failed:', error);
      throw error;
    }
  }

  // Helpers -------------------------------------------------------------------

  #toFirestoreDate(jsDate) {
    // Prefer Firestore Timestamp to avoid client clock drift where we can
    if (this.Timestamp && jsDate instanceof Date) {
      return this.Timestamp.fromDate(jsDate);
    }
    return jsDate; // fallback to Date; caller should .toDate() guard when reading
  }

  #fromFirestoreDate(value) {
    // Accepts Firestore Timestamp or JS Date; returns JS Date (or null)
    if (!value) return null;
    if (typeof value.toDate === 'function') return value.toDate();
    if (value instanceof Date) return value;
    try {
      return new Date(value);
    } catch {
      return null;
    }
  }

  #nowServerTimestamp() {
    return this.FieldValue?.serverTimestamp
      ? this.FieldValue.serverTimestamp()
      : new Date();
  }

  // Random 8-char uppercase code
  generateCodeString(length = 8) {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let out = '';
    for (let i = 0; i < length; i++) {
      out += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return out;
  }

  // Public API ----------------------------------------------------------------

  /**
   * 🎯 Generate a free access code
   * @param {Object} config
   *  - email?: string | null    -> restrict to an email
   *  - fanType?: 'dedicated' | 'diehard' | 'expert'  (frontend terms allowed)
   *  - uses?: number            -> how many times the code can be redeemed
   *  - expiresInDays?: number   -> days from now
   *  - description?: string
   *  - createdBy?: string
   */
  async generateFreeCode(config = {}) {
    await this.initialize();

    const {
      email = null,
      fanType = 'diehard', // default: most generous
      uses = 1,
      expiresInDays = 30,
      description = 'Free DieHard access',
      createdBy = 'admin',
    } = config;

    // Normalize to a frontend label, and compute backend mapping for clarity
    const normalizedFrontend = FAN_TYPE_MAPPINGS.normalize
      ? FAN_TYPE_MAPPINGS.normalize(fanType)
      : fanType;
    const backendType = FAN_TYPE_MAPPINGS.toBackend
      ? FAN_TYPE_MAPPINGS.toBackend(normalizedFrontend)
      : normalizedFrontend; // 'diehard' -> 'expert'

    const validFrontend = ['dedicated', 'diehard', 'expert'];
    if (!validFrontend.includes(normalizedFrontend)) {
      throw new Error(`Invalid fanType. Must be one of: ${validFrontend.join(', ')}`);
    }

    const freeCode = this.generateCodeString(8);
    const expiresAtJs = new Date(Date.now() + expiresInDays * 24 * 60 * 60 * 1000);

    const codeData = {
      code: freeCode,
      email: email || null, // optional restriction
      // Store both for transparency:
      fanType: normalizedFrontend,         // what UI shows (diehard/dedicated)
      backendFanType: backendType,         // what backend expects (expert/dedicated)
      totalUses: Math.max(1, Number(uses) || 1),
      usedCount: 0,
      description: String(description || ''),
      createdBy: String(createdBy || 'admin'),
      createdAt: this.#nowServerTimestamp(),
      expiresAt: this.#toFirestoreDate(expiresAtJs),
      isActive: true,
      usageHistory: [], // { email, userId?, usedAt, userAgent, timestamp }
      lastUsedAt: null,
    };

    try {
      await this.db.collection('freeCodes').doc(freeCode).set(codeData);
      const smartLink = `${window.location.origin}/?free=${freeCode}`;

      logger.success('🎉 FREE CODE GENERATED', {
        code: freeCode,
        fanType: normalizedFrontend,
        backendFanType: backendType,
        uses: codeData.totalUses,
        expiresAt: expiresAtJs.toISOString(),
        smartLink,
      });

      return { success: true, code: freeCode, smartLink, data: codeData };
    } catch (error) {
      console.error('❌ Error generating free code:', error);
      throw error;
    }
  }

  /**
   * 🔍 Validate and redeem a free code (consumes ONE use)
   * Returns { valid, fanType, backendFanType, remainingUses, error?, errorType? }
   */
  async validateAndUseFreeCode(code, userEmail = null, userId = null) {
    await this.initialize();

    const cleanCode = String(code || '').trim().toUpperCase();
    if (!cleanCode) {
      return { valid: false, error: 'Missing code', errorType: 'MISSING' };
    }

    try {
      const codeRef = this.db.collection('freeCodes').doc(cleanCode);
      const codeDoc = await codeRef.get();
      if (!codeDoc.exists) {
        return { valid: false, error: 'Invalid free code', errorType: 'NOT_FOUND' };
      }

      const data = codeDoc.data();

      if (!data.isActive) {
        return { valid: false, error: 'This free code has been deactivated', errorType: 'DEACTIVATED' };
      }

      const now = new Date();
      const expDate = this.#fromFirestoreDate(data.expiresAt);
      if (expDate && now > expDate) {
        return { valid: false, error: 'This free code has expired', errorType: 'EXPIRED' };
      }

      if ((data.usedCount || 0) >= (data.totalUses || 0)) {
        return { valid: false, error: 'This free code has been fully used', errorType: 'EXHAUSTED' };
      }

      // Email restriction (if code has an email bound)
      if (data.email && userEmail && String(data.email).toLowerCase() !== String(userEmail).toLowerCase()) {
        return { valid: false, error: 'This free code is not available for your account', errorType: 'EMAIL_MISMATCH' };
      }

      // Prevent duplicate redemption by same user/email (if trackable)
      const alreadyUsed = Array.isArray(data.usageHistory)
        ? data.usageHistory.some(u =>
            (userEmail && u.email && String(u.email).toLowerCase() === String(userEmail).toLowerCase()) ||
            (userId && u.userId && String(u.userId) === String(userId))
          )
        : false;

      if (alreadyUsed) {
        return { valid: false, error: 'You have already used this free code', errorType: 'ALREADY_USED' };
      }

      // Record usage atomically
      const usageRecord = {
        email: userEmail || null,
        userId: userId || null,
        usedAt: this.#nowServerTimestamp(),
        userAgent: (typeof navigator !== 'undefined' && navigator.userAgent) ? navigator.userAgent : 'n/a',
        timestamp: Date.now(),
      };

      await codeRef.update({
        usedCount: (data.usedCount || 0) + 1,
        usageHistory: [...(data.usageHistory || []), usageRecord],
        lastUsedAt: this.#nowServerTimestamp(),
      });

      const remaining = (data.totalUses || 0) - ((data.usedCount || 0) + 1);

      logger.success('✅ Free code used successfully', {
        code: cleanCode,
        fanType: data.fanType,
        backendFanType: data.backendFanType || FAN_TYPE_MAPPINGS.toBackend?.(data.fanType) || data.fanType,
        remainingUses: remaining,
        userEmail,
        userId,
      });

      return {
        valid: true,
        fanType: data.fanType,
        backendFanType: data.backendFanType || FAN_TYPE_MAPPINGS.toBackend?.(data.fanType) || data.fanType,
        description: data.description,
        remainingUses: remaining,
      };
    } catch (error) {
      console.error('❌ Error validating free code:', error);
      return { valid: false, error: 'Failed to validate free code', errorType: 'SYSTEM_ERROR' };
    }
  }

  /**
   * 📋 List free codes (admin)
   */
  async listFreeCodes(limit = 200) {
    await this.initialize();

    try {
      let query = this.db.collection('freeCodes').orderBy('createdAt', 'desc');
      if (limit) query = query.limit(limit);
      const snapshot = await query.get();

      const codes = snapshot.docs.map(doc => {
        const d = doc.data();
        return {
          id: doc.id,
          code: d.code || doc.id,
          fanType: d.fanType,
          backendFanType: d.backendFanType || FAN_TYPE_MAPPINGS.toBackend?.(d.fanType) || d.fanType,
          totalUses: d.totalUses || 0,
          usedCount: d.usedCount || 0,
          isActive: !!d.isActive,
          description: d.description || '',
          email: d.email || null,
          createdAt: this.#fromFirestoreDate(d.createdAt),
          expiresAt: this.#fromFirestoreDate(d.expiresAt),
          lastUsedAt: this.#fromFirestoreDate(d.lastUsedAt),
          usageHistory: Array.isArray(d.usageHistory) ? d.usageHistory : [],
        };
      });

      // Friendly console table
      console.table(
        codes.map(c => ({
          Code: c.code,
          FanType: c.fanType,
          Backend: c.backendFanType,
          Uses: `${c.usedCount}/${c.totalUses}`,
          Active: c.isActive ? '✅' : '❌',
          Expires: c.expiresAt ? c.expiresAt.toLocaleDateString() : '—',
          Email: c.email || 'Any',
          Description: c.description || '',
        }))
      );

      return codes;
    } catch (error) {
      console.error('❌ Error listing free codes:', error);
      throw error;
    }
  }

  /**
   * 📴 Deactivate a free code
   */
  async deactivateFreeCode(code) {
    await this.initialize();

    try {
      const clean = String(code || '').trim().toUpperCase();
      if (!clean) throw new Error('Code is required');

      const ref = this.db.collection('freeCodes').doc(clean);
      const doc = await ref.get();
      if (!doc.exists) throw new Error(`Free code ${clean} not found`);

      await ref.update({
        isActive: false,
        deactivatedAt: this.#nowServerTimestamp(),
      });

      logger.success(`🗑️ Free code ${clean} deactivated`);
      return { success: true };
    } catch (error) {
      console.error('❌ Error deactivating free code:', error);
      throw error;
    }
  }

  /**
   * 📊 Aggregate stats
   */
  async getFreeCodeStats() {
    await this.initialize();

    try {
      const snap = await this.db.collection('freeCodes').get();

      let totalCodes = 0;
      let activeCodes = 0;
      let expiredCodes = 0;
      let totalUses = 0;
      const fanTypeBreakdown = { dedicated: 0, expert: 0, diehard: 0 };

      const now = new Date();

      snap.docs.forEach(doc => {
        const d = doc.data();
        totalCodes += 1;
        if (d.isActive) activeCodes += 1;

        const exp = this.#fromFirestoreDate(d.expiresAt);
        if (exp && exp < now) expiredCodes += 1;

        totalUses += d.usedCount || 0;

        const ft = d.fanType || 'diehard';
        if (fanTypeBreakdown[ft] !== undefined) fanTypeBreakdown[ft] += 1;
      });

      const stats = { totalCodes, activeCodes, expiredCodes, totalUses, fanTypeBreakdown };

      console.table([
        {
          'Total Codes': totalCodes,
          Active: activeCodes,
          Expired: expiredCodes,
          'Total Uses': totalUses,
          Dedicated: fanTypeBreakdown.dedicated,
          Expert: fanTypeBreakdown.expert,
          DieHard: fanTypeBreakdown.diehard,
        },
      ]);

      return stats;
    } catch (error) {
      console.error('❌ Error getting free code stats:', error);
      throw error;
    }
  }
}

// Singleton instance
const freeCodeService = new FreeCodeService();

// Debug/admin helpers on window (optional)
if (typeof window !== 'undefined') {
  window.freeCodeService = freeCodeService;

  window.generateFreeCode = async (config = {}) => {
    try {
      const result = await freeCodeService.generateFreeCode({ fanType: 'diehard', ...config });
      console.log('\n📱 Share this message:');
      console.log(`"🎁 You've got free access! Click here: ${result.smartLink}"\n`);
      return result;
    } catch (e) {
      console.error('❌ Error generating free code:', e);
    }
  };

  window.listFreeCodes = async (limit) => {
    try {
      return await freeCodeService.listFreeCodes(limit);
    } catch (e) {
      console.error('❌ Error listing free codes:', e);
    }
  };

  window.deactivateFreeCode = async (code) => {
    try {
      await freeCodeService.deactivateFreeCode(code);
      console.log(`✅ Free code ${code} deactivated`);
    } catch (e) {
      console.error('❌ Error deactivating free code:', e);
    }
  };

  window.getFreeCodeStats = async () => {
    try {
      return await freeCodeService.getFreeCodeStats();
    } catch (e) {
      console.error('❌ Error getting free code stats:', e);
    }
  };

  window.freeCodeHelp = () => {
    console.log('\n🎁 FREE CODE SYSTEM - ADMIN COMMANDS');
    console.log('📝 Generate free code:');
    console.log('   await generateFreeCode()');
    console.log('   await generateFreeCode({ fanType: "expert", uses: 5 })');
    console.log('   await generateFreeCode({ email: "user@example.com" })');
    console.log('');
    console.log('📋 List all codes:');
    console.log('   await listFreeCodes()');
    console.log('');
    console.log('🗑️ Deactivate code:');
    console.log('   await deactivateFreeCode("ABC12345")');
    console.log('');
    console.log('📊 Get statistics:');
    console.log('   await getFreeCodeStats()');
  };
}

export default freeCodeService;
